import {
	Cookies,
	DateOnly,
	DateTime,
	isNullOrEmpty, Locale, LocaleId,
	SimpleStringFormatter,
	translation,
	Translations,
	TranslatorStringFormatter
} from "@2d-front-monorepo/core";
import {CurrencyDisplay, InstallationByHostData, InstallationId} from "@2d-front-monorepo/permits-business";
import {PermitTranslations} from "@2d-front-monorepo/shared-translations";
import {REACT_APP_HOSTNAME} from "../config";
import {logOff, permitsLogErrorToServer} from "../functions";
import {ReloadEntities, reloadPermits} from "../hooks/reloadPermits";
import {HttpContext} from "./HttpContext";
import {RefObject} from "react";

export interface GlobalConfig {
	installation: InstallationByHostData;
	initialStatus: ReloadEntities;
	locale: Locale;
	languageId: string;
	now: (dateOnly?: DateOnly) => DateTime;
	hostName: string;
}

function getCurrencyDisplay(display: CurrencyDisplay): "symbol" | "code" | "name" | "narrowSymbol" | undefined {
	switch (display) {
		case CurrencyDisplay.Symbol:
			return "symbol";
		case CurrencyDisplay.NarrowSymbol:
			return "narrowSymbol";
		case CurrencyDisplay.Code:
			return "code";
		case CurrencyDisplay.Name:
			return "name";

		default:
			return undefined;
	}
}

function getDateNow(offset: number, dateOnly?: DateOnly): DateTime {
	const dateTime = DateTime.fromTimestamp(Date.now() - offset);
	if (dateOnly == null) {
		return dateTime;
	}

	return DateTime.fromComponents(dateOnly.getYear(), dateOnly.getMonth(), dateOnly.getDay(), dateTime.getHours(), dateTime.getMinutes(), dateTime.getSeconds());
}

export class GlobalContext {
	private static _data: GlobalConfig | null = null;
	private static _dateOffset: number;
	private static _initializationPromise: Promise<void> | null = null;
	private static _isInitialized = false;

	static getData(): GlobalConfig {
		if (this._data == null) {
			throw new Error("GlobalContext is not initialized");
		}

		this._data.now = (dateOnly?: DateOnly) => getDateNow(this._dateOffset, dateOnly);
		return this._data;
	}

	static initialize(): Promise<void> {
		if (this._isInitialized) {
			return Promise.resolve();
		}

		if (this._initializationPromise) {
			return this._initializationPromise;
		}

		this._initializationPromise = this.performInitialization();
		return this._initializationPromise;
	}

	static isReady(): boolean {
		return this._isInitialized;
	}

	static reset(): void {
		this._data = null;
		this._initializationPromise = null;
		this._isInitialized = false;
	}

	static tryGetData(): GlobalConfig | null {
		return this._data;
	}

	private static async performInitialization(): Promise<void> {
		try {
			const hostName = REACT_APP_HOSTNAME;
			const installationData = await HttpContext.getClient().global.installation(hostName);
			const httpClient = HttpContext.getClient(installationData.id);

			let languageId = Cookies.get("language") as LocaleId;
			if (isNullOrEmpty(languageId) || installationData.languages.indexOf(languageId) === -1) {
				languageId = installationData.languages[0];
			}

			const installationIdRef: RefObject<InstallationId> = {current: installationData.id};

			const [translationEntries, initialStatus] = await Promise.all([
				httpClient.global.translations(installationData.id, languageId),
				reloadPermits(ReloadEntities.All, installationIdRef),
				// Preload tenant-specific components
				import("../customers/Registrations").then(({default: Registrations}) =>
					Registrations.preloadTenantComponents(installationData.slug, installationData.portal)
				).catch((e) => {
					permitsLogErrorToServer(e)
				})
			]);

			const translations = new Translations(languageId, [{
				localeId: languageId,
				translations: translationEntries
			}]);

			const culture: Locale = Locale.setCurrent(languageId, {
				currency: {
					currency: installationData.currency.code,
					display: getCurrencyDisplay(installationData.currency.display)
				}
			});

			Translations.setDefaultFormatter(new TranslatorStringFormatter(translations, new SimpleStringFormatter(culture)));
			// Translations.setDefaultFormatter(new TranslatorStringFormatter(translations, new TemplateStringFormatter(culture)));

			culture.addCustomFormat("short-time", {
				"hour": "2-digit",
				"minute": "2-digit"
			});

			document.getElementsByTagName("html")[0].setAttribute("lang", languageId);
			document.getElementsByTagName("title")[0].textContent = translation(PermitTranslations.page_title, {installation: installationData.name});

			const serverNow = installationData.now;
			this._dateOffset = Date.now() - Date.UTC(serverNow.getYear(), serverNow.getMonth() - 1, serverNow.getDay(), serverNow.getHours(), serverNow.getMinutes(), serverNow.getSeconds());

			this._data = {
				installation: installationData,
				initialStatus: initialStatus,
				locale: culture,
				languageId: languageId,
				now: (dateOnly?: DateOnly) => getDateNow(this._dateOffset, dateOnly),
				hostName: hostName
			};

			this._isInitialized = true;

			if (installationData.id !== installationIdRef.current) {
				await logOff();
			}

			HttpContext.reset();

		} finally {
			this._initializationPromise = null;
		}
	}
}
